import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/Toast';
import { confirm } from '../utils/confirm';
import { fmtVND, fmtDate } from '../utils/format';
import {
  listWeeklyWeeks,
  getWeeklyWeek,
  getWeeklyDeadline,
  setWeeklyDeadline,
  resetWeeklyDept,
} from '../api/weeklyReport';

const WEEKDAYS = ['', 'Thứ 2', 'Thứ 3', 'Thứ 4', 'Thứ 5', 'Thứ 6', 'Thứ 7', 'Chủ nhật'];
const fmtDT = (x) => {
  if (!x) return '';
  const d = new Date(x);
  const p = (n) => String(n).padStart(2, '0');
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

// Nhãn trạng thái ngắn gọn cho từng phòng trong ô tuần.
function deptStatusLabel(dp) {
  if (dp.status === 'submitted') return { t: '✓ Đã nộp', c: 'var(--ok)' };
  if (dp.status === 'late') return { t: '⏰ Muộn', c: 'var(--warn)' };
  if (dp.status === 'partial') return { t: `● ${dp.note || ''}`.trim(), c: 'var(--warn)' };
  return { t: dp.note ? `✕ ${dp.note}` : '✕ Chưa', c: 'var(--danger)' };
}
const shortName = (name) => String(name || '').replace(/^Phòng\s+/, '');

// Nội dung báo cáo của 1 phòng (hiển thị dưới danh sách khi bấm chọn).
function DeptDetail({ dept }) {
  if (!dept) return null;
  const d = dept.detail;
  if (dept.status === 'missing') return <div className="empty">Phòng này chưa nộp báo cáo tuần.</div>;
  if (!d) return <div className="empty">Không có nội dung báo cáo.</div>;

  if (dept.detailType === 'business') {
    return (
      <>
        <div className="row" style={{ gap: 24, flexWrap: 'wrap', margin: '4px 0 12px' }}>
          <div><div className="small muted">Doanh thu</div><div style={{ fontSize: 20, fontWeight: 800, color: 'var(--accent2)' }}>{fmtVND(d.totalRevenue)}</div></div>
          <div><div className="small muted">Khoán</div><div style={{ fontSize: 20, fontWeight: 800 }}>{fmtVND(d.totalTarget || 0)}</div></div>
          <div><div className="small muted">Tỷ lệ HT</div><div style={{ fontSize: 20, fontWeight: 800 }}>{d.donePct || 0}%</div></div>
          <div><div className="small muted">Tỷ lệ cọc</div><div style={{ fontSize: 20, fontWeight: 800 }}>{d.ratio || 0}%</div></div>
        </div>
        {(d.members || []).length > 0 && (
          <div className="tbl-wrap framed" style={{ marginBottom: 12 }}>
            <table style={{ fontSize: 13 }}>
              <thead>
                <tr>
                  <th>Mã NV</th>
                  <th>Nhân viên</th>
                  <th className="r">Doanh thu</th>
                  <th className="r">Khoán</th>
                  <th className="r">HT</th>
                  <th className="r">Cọc</th>
                </tr>
              </thead>
              <tbody>
                {d.members.map((m, i) => (
                  <tr key={i}>
                    <td className="small muted">{m.employeeCode || '—'}</td>
                    <td>
                      {m.name}
                      {m.role === 'TP' && <span className="tag" style={{ marginLeft: 6, color: 'var(--accent)' }}>TP</span>}
                    </td>
                    <td className="r">{fmtVND(m.revenue)}</td>
                    <td className="r">{m.target ? fmtVND(m.target) : <span className="muted">—</span>}</td>
                    <td className="r">{m.target ? `${m.donePct || 0}%` : '—'}</td>
                    <td className="r">{m.ratio || 0}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {['doneThisWeek', 'doneResult', 'nextWeekPlan', 'nextWeekGoal'].some((k) => d[k]) && (
          <div className="card" style={{ background: 'var(--panel2)' }}>
            {[['Công việc đã thực hiện', 'doneThisWeek'], ['Kết quả', 'doneResult'], ['Kế hoạch tuần tới', 'nextWeekPlan'], ['Mục tiêu', 'nextWeekGoal']].map(([lb, k]) =>
              d[k] ? (
                <div key={k} style={{ marginBottom: 8 }}>
                  <div className="small muted" style={{ fontWeight: 700 }}>{lb}</div>
                  <div className="small" style={{ whiteSpace: 'pre-wrap' }}>{d[k]}</div>
                </div>
              ) : null
            )}
          </div>
        )}
      </>
    );
  }
  if (dept.detailType === 'finance') {
    return (
      <div className="row" style={{ gap: 24, flexWrap: 'wrap' }}>
        <div><div className="small muted">Tổng công nợ</div><div style={{ fontSize: 20, fontWeight: 800, color: 'var(--danger)' }}>{fmtVND(d.totalDebt || 0)}</div></div>
        <div><div className="small muted">KH nợ lâu</div><div style={{ fontSize: 20, fontWeight: 800 }}>{d.longCount || 0}</div></div>
        {d.savedBy && <div><div className="small muted">Chốt bởi</div><div style={{ fontWeight: 700 }}>{d.savedBy}</div></div>}
      </div>
    );
  }
  if (dept.detailType === 'cskh') {
    return (
      <div className="tbl-wrap framed">
        <table style={{ fontSize: 13 }}>
          <thead><tr><th>Nhân viên</th><th className="c">Đã liên hệ</th><th>Trạng thái</th></tr></thead>
          <tbody>
            {(d.reports || []).map((r, i) => (
              <tr key={i}>
                <td>{r.name}</td>
                <td className="c">{r.totalContacted || 0}</td>
                <td>{r.submittedAt ? <span style={{ color: 'var(--ok)' }}>✓ {fmtDT(r.submittedAt)}</span> : <span style={{ color: 'var(--danger)' }}>Chưa nộp</span>}</td>
              </tr>
            ))}
            {!(d.reports || []).length && <tr><td colSpan={3} className="empty">Chưa có nhân viên CSKH nộp.</td></tr>}
          </tbody>
        </table>
      </div>
    );
  }
  if (dept.detailType === 'purchase') {
    const delta = (a, b) => {
      const dv = (Number(a) || 0) - (Number(b) || 0);
      if (!dv) return <span className="muted">0</span>;
      return <span style={{ color: dv > 0 ? '#16a34a' : 'var(--danger)' }}>{dv > 0 ? '▲' : '▼'} {Math.abs(dv)}</span>;
    };
    const sum = (rows, f) => (rows || []).reduce((a, r) => a + (Number(r[f]) || 0), 0);
    // Tiền theo tệ gốc: JP -> Yên (¥), US -> Đô ($).
    const cur = (amt, region) => (region === 'US' ? '$' : '¥') + Number(amt || 0).toLocaleString('vi-VN');
    // Tổng tiền tách theo tệ (không cộng ¥ với $).
    const moneyByCur = (rows, f) => {
      const jp = (rows || []).filter((r) => r.region !== 'US').reduce((a, r) => a + (Number(r[f]) || 0), 0);
      const us = (rows || []).filter((r) => r.region === 'US').reduce((a, r) => a + (Number(r[f]) || 0), 0);
      return (
        <>
          {jp ? <div>¥{jp.toLocaleString('vi-VN')}</div> : null}
          {us ? <div>${us.toLocaleString('vi-VN')}</div> : null}
          {!jp && !us ? '—' : null}
        </>
      );
    };
    return (
      <>
        <div className="small muted" style={{ marginBottom: 6 }}>
          Lũy kế tháng {d.monthLabel} (1 → {d.asOfLabel}) · {d.days} ngày có báo cáo
          {d.prevMonthLabel ? ` · so với tháng ${d.prevMonthLabel} (1 → ${d.prevAsOfLabel})` : ''}
        </div>
        <div className="tbl-wrap framed" style={{ marginBottom: 12 }}>
          <table style={{ fontSize: 13 }}>
            <thead>
              <tr>
                <th>Nguồn</th><th className="c">Tuyến</th>
                <th className="r">Đơn (1→{d.asOfLabel})</th>
                <th className="r">Đơn (1→{d.prevAsOfLabel})</th>
                <th className="r">Chênh lệch</th>
                <th className="r">Số tiền (1→{d.asOfLabel})</th>
                <th className="r">Số tiền (1→{d.prevAsOfLabel})</th>
              </tr>
            </thead>
            <tbody>
              {(d.rows || []).map((r, i) => (
                <tr key={i}>
                  <td>{r.source || '—'}</td><td className="c">{r.region || '—'}</td>
                  <td className="r">{r.totalOrders || 0}</td>
                  <td className="r muted">{r.prevTotalOrders || 0}</td>
                  <td className="r">{delta(r.totalOrders, r.prevTotalOrders)}</td>
                  <td className="r">{cur(r.totalAmount, r.region)}</td>
                  <td className="r muted">{cur(r.prevTotalAmount, r.region)}</td>
                </tr>
              ))}
              {!(d.rows || []).length && <tr><td colSpan={7} className="empty">Chưa có dữ liệu tháng.</td></tr>}
              {(d.rows || []).length > 0 && (
                <tr style={{ fontWeight: 700, borderTop: '2px solid var(--border)' }}>
                  <td colSpan={2}>Tổng</td>
                  <td className="r">{sum(d.rows, 'totalOrders')}</td>
                  <td className="r muted">{sum(d.rows, 'prevTotalOrders')}</td>
                  <td className="r">{delta(sum(d.rows, 'totalOrders'), sum(d.rows, 'prevTotalOrders'))}</td>
                  <td className="r">{moneyByCur(d.rows, 'totalAmount')}</td>
                  <td className="r muted">{moneyByCur(d.rows, 'prevTotalAmount')}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {d.monthNote && String(d.monthNote).trim() && (
          <div className="card" style={{ background: 'var(--panel2)', marginBottom: 8 }}>
            <div className="small muted" style={{ fontWeight: 700, marginBottom: 6 }}>📝 Ghi chú phòng mua — tháng {d.monthLabel}</div>
            <div className="small" style={{ whiteSpace: 'pre-wrap' }}>{d.monthNote}</div>
          </div>
        )}
        {(d.notes || []).length > 0 && (
          <div className="card" style={{ background: 'var(--panel2)', marginBottom: 0 }}>
            <div className="small muted" style={{ fontWeight: 700, marginBottom: 6 }}>Ghi chú theo ngày</div>
            {d.notes.map((n, i) => (
              <div key={i} className="small" style={{ marginBottom: 4, whiteSpace: 'pre-wrap' }}>
                <span className="muted">{fmtDate(n.date)}{n.source ? ` · ${n.source}` : ''}: </span>{n.note}
              </div>
            ))}
          </div>
        )}
      </>
    );
  }
  return null;
}

export default function WeeklyReportPage() {
  const { me } = useAuth();
  const toast = useToast();
  const admin = me.role === 'GD' || me.role === 'CV';
  const [weeks, setWeeks] = useState([]);
  const [deadline, setDeadline] = useState({ weekday: 4, time: '16:30' });
  const [activeKey, setActiveKey] = useState(null);
  const [week, setWeek] = useState(null); // chi tiết tuần đang chọn
  const [deptKey, setDeptKey] = useState(null); // phòng đang mở
  const [loading, setLoading] = useState(true);
  const [savingDl, setSavingDl] = useState(false);
  const scroller = useRef(null);

  useEffect(() => {
    Promise.all([
      listWeeklyWeeks().then((r) => { setWeeks(r.weeks || []); if (r.deadline) setDeadline(r.deadline); return r.weeks; }),
      getWeeklyDeadline().then(setDeadline).catch(() => {}),
    ])
      .then(([ws]) => { if (ws && ws.length) openWeek(ws[0].weekKey); })
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function openWeek(weekKey) {
    setActiveKey(weekKey);
    setDeptKey(null);
    const w = await getWeeklyWeek(weekKey);
    setWeek(w);
  }

  // Bấm 1 phòng trong ô tuần: nạp chi tiết tuần đó (nếu chưa) rồi mở báo cáo phòng.
  async function openDept(weekKey, dk) {
    let w = week;
    if (activeKey !== weekKey || !w) {
      setActiveKey(weekKey);
      w = await getWeeklyWeek(weekKey);
      setWeek(w);
    }
    setDeptKey((prev) => (prev === dk && activeKey === weekKey ? null : dk));
  }

  async function saveDeadline() {
    setSavingDl(true);
    try {
      await setWeeklyDeadline(deadline.weekday, deadline.time);
      toast('Đã lưu hạn nộp mặc định');
      const r = await listWeeklyWeeks();
      setWeeks(r.weeks || []);
      if (activeKey) openWeek(activeKey);
    } catch (err) {
      toast(err.response?.data?.message || 'Lỗi lưu hạn nộp', 'danger');
    } finally {
      setSavingDl(false);
    }
  }

  const activeDept = week && deptKey ? (week.departments || []).find((d) => d.key === deptKey) : null;

  // GĐ/CV xoá báo cáo tuần của 1 phòng để phòng đó nộp lại từ đầu.
  async function onResetDept() {
    if (!activeDept || !activeKey) return;
    const ok = await confirm(`Xoá báo cáo "${activeDept.name}" của ${week?.label || 'tuần này'}?\nPhòng sẽ phải nộp lại từ đầu. Không thể hoàn tác.`);
    if (!ok) return;
    try {
      const r = await resetWeeklyDept(activeKey, activeDept.key);
      toast(r.removed ? 'Đã xoá — phòng có thể nộp lại' : 'Không có báo cáo để xoá');
      await openWeek(activeKey);
      const rr = await listWeeklyWeeks();
      setWeeks(rr.weeks || []);
    } catch (e) {
      toast(e.response?.data?.message || 'Lỗi xoá báo cáo', 'danger');
    }
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h2>🗓️ Báo cáo tuần</h2>
          <div className="sub">Trạng thái nộp báo cáo của các phòng theo tuần. Bấm một phòng để xem báo cáo.</div>
        </div>
        {admin && (
          <div className="row" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span className="small muted">Hạn nộp:</span>
            <select value={deadline.weekday} onChange={(e) => setDeadline((p) => ({ ...p, weekday: Number(e.target.value) }))} style={{ width: 'auto' }}>
              {[1, 2, 3, 4, 5, 6, 7].map((w) => <option key={w} value={w}>{WEEKDAYS[w]}</option>)}
            </select>
            <input type="time" value={deadline.time} onChange={(e) => setDeadline((p) => ({ ...p, time: e.target.value }))} style={{ width: 'auto' }} />
            <button className="btn sm" disabled={savingDl} onClick={saveDeadline}>{savingDl ? 'Đang lưu…' : 'Lưu hạn'}</button>
          </div>
        )}
      </div>

      <div className="card">
        {loading ? (
          <div className="empty">Đang tải…</div>
        ) : !weeks.length ? (
          <div className="empty">Chưa có dữ liệu tuần.</div>
        ) : (
          <>
            {/* Timeline theo tuần: mỗi ô liệt kê các phòng + trạng thái. Bấm phòng -> xem báo cáo bên dưới. */}
            <div className="tl-wrap" style={{ marginBottom: 14 }}>
              <button className="tl-arrow" onClick={() => scroller.current?.scrollBy({ left: -400, behavior: 'smooth' })}>‹</button>
              <div className="tl-scroll" ref={scroller}>
                {weeks.map((w) => (
                  <div key={w.weekKey} className={'cslog-cell' + (activeKey === w.weekKey ? ' thu' : '')} style={{ flex: '0 0 194px' }}>
                    <div className="cslog-head cslog-click" onClick={() => openWeek(w.weekKey)} title="Xem cả tuần">{w.label}</div>
                    {(w.departments || []).map((dp) => {
                      const s = deptStatusLabel(dp);
                      const sel = activeKey === w.weekKey && deptKey === dp.key;
                      return (
                        <div
                          key={dp.key}
                          onClick={() => openDept(w.weekKey, dp.key)}
                          title={dp.name}
                          style={{ display: 'flex', justifyContent: 'space-between', gap: 6, fontSize: 12, padding: '3px 5px', borderRadius: 6, cursor: 'pointer', background: sel ? 'var(--panel2)' : undefined }}
                        >
                          <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{shortName(dp.name)}</span>
                          <span style={{ color: s.c, whiteSpace: 'nowrap' }}>{s.t}</span>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
              <button className="tl-arrow" onClick={() => scroller.current?.scrollBy({ left: 400, behavior: 'smooth' })}>›</button>
            </div>

            {/* Chi tiết báo cáo phòng đang chọn */}
            {activeDept ? (
              <div className="card" style={{ marginBottom: 0 }}>
                <div className="row between" style={{ alignItems: 'flex-start', gap: 8 }}>
                  <h3 style={{ marginTop: 0 }}>📄 Báo cáo — {activeDept.name}{week ? ` · ${week.label}` : ''}</h3>
                  {admin && activeDept.status !== 'missing' && (
                    <button className="btn ghost sm" style={{ color: 'var(--danger)', borderColor: 'var(--danger)', flexShrink: 0 }} onClick={onResetDept}>
                      🗑 Xoá / Làm lại
                    </button>
                  )}
                </div>
                {activeDept.submittedAt && (activeDept.status === 'submitted' || activeDept.status === 'late') && (
                  <div className="small muted" style={{ marginTop: -6, marginBottom: 8 }}>
                    Nộp lúc {fmtDT(activeDept.submittedAt)}{activeDept.head ? ` · ${activeDept.head}` : ''}
                  </div>
                )}
                <DeptDetail dept={activeDept} />
              </div>
            ) : (
              <div className="small muted">Bấm một phòng trong ô tuần để xem báo cáo.</div>
            )}
          </>
        )}
      </div>
    </>
  );
}
